import { useEffect } from "react";
import { usePurchaseContext } from "../../contexts/purchase";
import { useHeyFlowLeadContext } from "../../contexts/heyFlowLead";
import { usePopupsContext } from "../../contexts/popups";
import { useDebouncedValue } from "../../hooks/useDebounceValue";
import { pruefeAbholung, ENERGIE_ZUSCHLAG_EUR } from "../../data/abholung";
import { CountrySelect } from "../CountrySelect";
import { FancyFormTextInput } from "../FancyTextInput";
import { FormErrorMessage } from "../FormErrorMessage";
import { PhoneInput } from "../PhoneInput";

export function PurchasePickupAddress() {
  const {
    purchaseInformation,
    errors,
    updateField,
    onPickupAddressCountryChange,
    pickupAddressPredictions,
    selectPickupAddressPrediction,
    cancelPickupAddressPrediction,
  } = usePurchaseContext();

  const { value, changeCartItem } = useHeyFlowLeadContext();
  const { setPopupType } = usePopupsContext();

  // Horsia: Die Abholung erfolgt IMMER an einer anderen Adresse (Stall/Klinik),
  // nicht an der Rechnungsadresse. Daher ist die Abhol-PLZ = pickupAddressZipCode.
  const effectivePlz = purchaseInformation?.pickupAddressZipCode;

  const debouncedPlz = useDebouncedValue(effectivePlz ?? "", 800);

  // Debounced PLZ-Änderung → Abhol-Preis neu berechnen oder Popup wenn nicht anfahrbar.
  useEffect(() => {
    if (!debouncedPlz || !value) return;
    if (!/^\d{5}$/.test(debouncedPlz)) return;

    const result = pruefeAbholung(debouncedPlz);

    if (!result.anfahrbar) {
      setPopupType({ type: "negativeLocation" });
      return;
    }

    const transportIndex = value.cart.findIndex((i) => i.type === "transport");
    if (transportIndex === -1) return;
    const current = value.cart[transportIndex];
    const newTransportPrice = (result.preis_eur ?? 0) - ENERGIE_ZUSCHLAG_EUR;

    if (current.price !== newTransportPrice) {
      changeCartItem(
        {
          ...current,
          price: newTransportPrice,
          name: `Abholung (PLZ ${debouncedPlz.substring(0, 2)}xxx)`,
        },
        transportIndex
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlz]);

  return (
    <>
      <div className="font-bold mt-7 mb-3">Abholung deines Pferdes</div>
      <div className="text-sm text-neutral-600 mb-3">
        Die Abholung erfolgt in der Regel an einem Stall oder einer Klinik. Bitte gib uns die Adresse
        und einen Ansprechpartner vor Ort.
      </div>
      <CountrySelect
        country={purchaseInformation?.pickupAddressCountry}
        setCountry={(country) => {
          updateField("pickupAddressCountry", country);
          onPickupAddressCountryChange(country);
        }}
        hasError={errors.pickupAddressCountry !== undefined}
      />
      <div className="pt-3" />
      <FancyFormTextInput
        label="Stall / Klinik / Name"
        type="text"
        formKey="pickupAddressPracticeName"
        formValue={purchaseInformation}
        setFormValue={updateField}
        errors={errors}
      />
      <div className="pt-3" />
      <FancyFormTextInput
        label="Straße und Hausnummer"
        type="text"
        formKey="pickupAddressStreet"
        formValue={purchaseInformation}
        setFormValue={updateField}
        errors={errors}
        mapsPredictions={pickupAddressPredictions}
        cancelPredictions={cancelPickupAddressPrediction}
        selectPrediction={selectPickupAddressPrediction}
      />
      <div className="flex gap-4 pt-3">
        <FancyFormTextInput
          label="Postleitzahl"
          type="text"
          formKey="pickupAddressZipCode"
          formValue={purchaseInformation}
          setFormValue={updateField}
          errors={errors}
        />
        <FancyFormTextInput
          label="Stadt"
          type="text"
          formKey="pickupAddressCity"
          formValue={purchaseInformation}
          setFormValue={updateField}
          errors={errors}
        />
      </div>
      <div className="pt-3" />
      <PhoneInput
        countryCode={purchaseInformation?.pickupAddressPhoneCountryCode}
        number={purchaseInformation?.pickupAddressPhoneNumber}
        setCountryCode={(countryCode) => updateField("pickupAddressPhoneCountryCode", countryCode)}
        setNumber={(number) => updateField("pickupAddressPhoneNumber", number)}
        id="pickup-address-phone"
        hasError={errors.pickupAddressPhoneNumber !== undefined}
        isOptional={false}
      />
      <FormErrorMessage
        formKeys={[
          "pickupAddressCountry",
          "pickupAddressPracticeName",
          "pickupAddressStreet",
          "pickupAddressZipCode",
          "pickupAddressCity",
          "pickupAddressPhoneCountryCode",
          "pickupAddressPhoneNumber",
        ]}
      />
    </>
  );
}
